import { useContext, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { t } from "i18next";
import { MagicStar, Trash } from "iconsax-react";
import DBButton from "../../../../component/global/DBButton";
import DBFieldBlock from "../../../../component/global/DBFieldBlock";
import DBInput from "../../../../component/global/DBInput";
import DBModal from "../../../../component/global/DBModal";
import DBSelect from "../../../../component/global/DBSelect";
import DBTextArea from "../../../../component/global/DBTextArea";
import { ToastContext } from "../../../../component/global/ToastContext";
import { useRequest } from "../../../../hooks/helperData/useRequest";
import {
    AUTOMATION_GROUPS_AI_APPLY,
    AUTOMATION_GROUPS_AI_ASSIGN_ITEM_TYPES,
    AUTOMATION_GROUPS_AI_GENERATE,
    GET_COMPANY_ROLES_LIST,
    GET_USER_OF_COMPANY,
} from "../../../../helperData/ApiAddress";
import "./ai-handling-styles.scss";
import { AiCoinHint, isInsufficientCoins, useAiCoinGate } from "../../../../features/ai-billing/AiCoinGate";

const INITIAL_ANSWERS = {
    handling_scope: "",
    grouping_basis: "",
    supervisor_model: "separate_supervisor",
    referral_preference: "",
    exclusions: "",
    group_count: 4,
};

const userLabel = (user) => {
    if (!user) return "";
    const name = `${user.first_name || ""} ${user.last_name || ""}`.trim();
    const role = user.role ? ` (${user.role})` : "";
    return `${name || t("Organizational.Profile.MakeGroup.Guest.User")}${role}`;
};

const isValidGroup = (group) =>
    Boolean(
        group?.name?.trim() &&
            group?.head_user_id &&
            Array.isArray(group?.member_user_ids) &&
            group.member_user_ids.includes(group.head_user_id)
    );

export default function AIHandlingGroupModal({
    showModal,
    onHide,
    onSuccess,
    companyId,
}) {
    const toast = useContext(ToastContext);
    const [step, setStep] = useState(0);
    const [answers, setAnswers] = useState(INITIAL_ANSWERS);
    const [groups, setGroups] = useState();
    const [assignments, setAssignments] = useState([]);
    const [pendingAssignments, setPendingAssignments] = useState();
    const [users, setUsers] = useState([]);
    const [rolesGate, setRolesGate] = useState("checking");
    const [error, setError] = useState("");

    const [getRoles, rolesLoader] = useRequest(GET_COMPANY_ROLES_LIST, "GET");
    const [getUsers] = useRequest(
        GET_USER_OF_COMPANY.replace(
            "{id}",
            companyId || sessionStorage.getItem("usercompany") || ""
        ),
        "GET"
    );
    const [generateGroups, generateLoader] = useRequest(
        AUTOMATION_GROUPS_AI_GENERATE,
        "POST"
    );
    const [applyGroups, applyLoader] = useRequest(
        AUTOMATION_GROUPS_AI_APPLY,
        "POST"
    );
    const [assignItemTypes, assignLoader] = useRequest(
        AUTOMATION_GROUPS_AI_ASSIGN_ITEM_TYPES,
        "POST"
    );
    const { confirmAndRun, openFromError, gate } = useAiCoinGate();

    const userOptions = useMemo(
        () =>
            users.map((user) => ({
                value: user.id,
                label: userLabel(user),
            })),
        [users]
    );

    useEffect(() => {
        if (!showModal) return;

        setStep(0);
        setAnswers(INITIAL_ANSWERS);
        setGroups(undefined);
        setAssignments([]);
        setPendingAssignments(undefined);
        setError("");
        setRolesGate("checking");

        getRoles()
            .then((response) => {
                const roleList = Array.isArray(response)
                    ? response
                    : response?.results || [];
                const withUsers = roleList.filter((role) => role?.user?.id);
                if (!roleList.length) {
                    setRolesGate("missing");
                    return;
                }
                if (!withUsers.length) {
                    setRolesGate("no_users");
                    return;
                }
                setRolesGate("ready");
            })
            .catch(() => setRolesGate("missing"));

        if (companyId) {
            getUsers()
                .then((response) => {
                    const list = Array.isArray(response)
                        ? response
                        : response?.results || [];
                    setUsers(
                        list.map((item) => ({
                            id: item.id,
                            first_name: item.first_name,
                            last_name: item.last_name,
                            role: item.role,
                        }))
                    );
                })
                .catch(() => setUsers([]));
        }
    }, [showModal, companyId]);

    const updateAnswer = (name, value) => {
        setAnswers({ ...answers, [name]: value });
        setError("");
    };

    const validateStep = () => {
        const requiredFields = [
            "handling_scope",
            "grouping_basis",
            null,
            null,
            null,
        ];
        const field = requiredFields[step];
        if (field && !String(answers[field] || "").trim()) {
            setError(t("ai.handling.required"));
            return false;
        }
        return true;
    };

    const nextStep = () => {
        if (validateStep()) setStep(step + 1);
    };

    const requestGroups = () => {
        if (!validateStep()) return;
        confirmAndRun("handling_groups", () => {
            generateGroups(answers)
                .then((response) => {
                    setGroups(response.groups || []);
                    setAssignments(response.item_type_assignments || []);
                    if (response.users?.length) {
                        setUsers(response.users);
                    }
                    setError("");
                })
                .catch((err) => {
                    if (isInsufficientCoins(err)) {
                        openFromError(err);
                        return;
                    }
                    const code = err?.response?.data?.code;
                    const detail = err?.response?.data?.detail;
                    if (code === "roles_required") {
                        setRolesGate("missing");
                        toast(detail || t("ai.handling.roles.required"), "danger");
                        return;
                    }
                    if (code === "role_users_required") {
                        setRolesGate("no_users");
                        toast(
                            detail || t("ai.handling.role.users.required"),
                            "danger"
                        );
                        return;
                    }
                    toast(t("ai.handling.generate.error"), "danger");
                });
        });
    };

    const updateGroup = (index, patch) => {
        setGroups(
            groups.map((group, groupIndex) =>
                groupIndex === index ? { ...group, ...patch } : group
            )
        );
        setError("");
    };

    const saveGroups = () => {
        if (!groups?.length || groups.some((group) => !isValidGroup(group))) {
            setError(t("ai.handling.groups.required"));
            return;
        }

        applyGroups({
            groups: groups.map((group) => ({
                key: group.key,
                name: group.name,
                purpose: group.purpose || "",
                head_user_id: group.head_user_id,
                member_user_ids: group.member_user_ids,
                assign_group_keys: group.assign_group_keys || [],
            })),
            item_type_assignments: assignments,
        })
            .then((response) => {
                toast(
                    t("ai.handling.save.success", {
                        count: response.created_count,
                    }),
                    "success"
                );
                const nextAssignments = response.item_type_assignments || [];
                if (nextAssignments.length) {
                    setPendingAssignments(nextAssignments);
                    setGroups(undefined);
                    return;
                }
                onHide?.();
                onSuccess?.(response);
            })
            .catch(() => toast(t("ai.handling.save.error"), "danger"));
    };

    const confirmAssignments = () => {
        assignItemTypes({
            assignments: pendingAssignments.map((item) => ({
                item_type_id: item.item_type_id,
                handling_group_id: item.handling_group_id,
                supervisor_group_ids: item.supervisor_group_ids,
            })),
        })
            .then((response) => {
                toast(
                    t("ai.handling.assign.success", {
                        count: response.updated_count,
                    }),
                    "success"
                );
                onHide?.();
                onSuccess?.(response);
            })
            .catch(() => toast(t("ai.handling.assign.error"), "danger"));
    };

    const skipAssignments = () => {
        onHide?.();
        onSuccess?.();
    };

    const renderQuestion = () => {
        switch (step) {
            case 0:
                return (
                    <DBFieldBlock
                        title={t("ai.handling.q1")}
                        required
                        error={error}
                    >
                        <DBTextArea
                            rows={4}
                            value={answers.handling_scope}
                            placeholder={t("ai.handling.q1.placeholder")}
                            onChange={(event) =>
                                updateAnswer(
                                    "handling_scope",
                                    event.target.value
                                )
                            }
                        />
                    </DBFieldBlock>
                );
            case 1:
                return (
                    <DBFieldBlock
                        title={t("ai.handling.q2")}
                        required
                        error={error}
                    >
                        <DBTextArea
                            rows={4}
                            value={answers.grouping_basis}
                            placeholder={t("ai.handling.q2.placeholder")}
                            onChange={(event) =>
                                updateAnswer(
                                    "grouping_basis",
                                    event.target.value
                                )
                            }
                        />
                    </DBFieldBlock>
                );
            case 2:
                return (
                    <DBFieldBlock title={t("ai.handling.q3")} required>
                        <DBSelect
                            size="large"
                            value={answers.supervisor_model}
                            onChange={(value) =>
                                updateAnswer("supervisor_model", value)
                            }
                            options={[
                                {
                                    value: "separate_supervisor",
                                    label: t(
                                        "ai.handling.supervisor.separate"
                                    ),
                                },
                                {
                                    value: "same_as_handling",
                                    label: t("ai.handling.supervisor.same"),
                                },
                                {
                                    value: "mixed",
                                    label: t("ai.handling.supervisor.mixed"),
                                },
                            ]}
                        />
                    </DBFieldBlock>
                );
            case 3:
                return (
                    <div className="ai-question-fields">
                        <DBFieldBlock title={t("ai.handling.q4")}>
                            <DBTextArea
                                rows={4}
                                value={answers.referral_preference}
                                placeholder={t("ai.handling.q4.placeholder")}
                                onChange={(event) =>
                                    updateAnswer(
                                        "referral_preference",
                                        event.target.value
                                    )
                                }
                            />
                        </DBFieldBlock>
                        <DBFieldBlock title={t("ai.handling.exclusions")}>
                            <DBTextArea
                                rows={3}
                                value={answers.exclusions}
                                placeholder={t(
                                    "ai.handling.exclusions.placeholder"
                                )}
                                onChange={(event) =>
                                    updateAnswer(
                                        "exclusions",
                                        event.target.value
                                    )
                                }
                            />
                        </DBFieldBlock>
                    </div>
                );
            default:
                return (
                    <DBFieldBlock title={t("ai.handling.q5")} required>
                        <DBSelect
                            size="large"
                            value={answers.group_count}
                            onChange={(value) =>
                                updateAnswer("group_count", value)
                            }
                            options={[3, 4, 5, 6, 8].map((count) => ({
                                value: count,
                                label: t(`ai.handling.count.${count}`),
                            }))}
                        />
                    </DBFieldBlock>
                );
        }
    };

    const renderGate = () => {
        if (rolesGate === "checking" || rolesLoader) {
            return (
                <div className="ai-handling-gate" aria-live="polite">
                    <p>{t("loading")}…</p>
                </div>
            );
        }

        const message =
            rolesGate === "no_users"
                ? t("ai.handling.role.users.required")
                : t("ai.handling.roles.required");

        return (
            <div className="ai-handling-gate" role="status" aria-live="polite">
                <h3>{message}</h3>
                <p>{t("ai.handling.roles.required.help")}</p>
                <Link
                    className="ai-handling-gate-link"
                    to="/dashboard/profile?tab=3"
                    onClick={onHide}
                >
                    {t("ai.handling.roles.go.profile")}
                </Link>
            </div>
        );
    };

    const renderPreview = () => (
        <div className="ai-preview">
            <h3>{t("ai.handling.preview.title")}</h3>
            <p>{t("ai.handling.preview.description")}</p>
            {error && (
                <div className="ai-error" role="alert">
                    {error}
                </div>
            )}
            <ul className="ai-handling-list">
                {groups.map((group, index) => (
                    <li key={group.key || index} className="ai-handling-card">
                        <div className="ai-handling-card-head">
                            <DBFieldBlock title={t("ai.handling.group.name")}>
                                <DBInput
                                    value={group.name}
                                    size="large"
                                    onChange={(event) =>
                                        updateGroup(index, {
                                            name: event.target.value,
                                        })
                                    }
                                />
                            </DBFieldBlock>
                            <button
                                type="button"
                                className="ai-tree-delete"
                                aria-label={t("ai.handling.group.remove")}
                                title={t("ai.handling.group.remove")}
                                onClick={() =>
                                    setGroups(
                                        groups.filter(
                                            (_, itemIndex) =>
                                                itemIndex !== index
                                        )
                                    )
                                }
                            >
                                <Trash size={20} aria-hidden="true" />
                            </button>
                        </div>
                        <DBFieldBlock title={t("ai.handling.group.purpose")}>
                            <DBTextArea
                                rows={2}
                                value={group.purpose || ""}
                                onChange={(event) =>
                                    updateGroup(index, {
                                        purpose: event.target.value,
                                    })
                                }
                            />
                        </DBFieldBlock>
                        <div className="ai-step-grid">
                            <DBFieldBlock title={t("ai.handling.group.head")}>
                                <DBSelect
                                    size="large"
                                    value={group.head_user_id}
                                    options={userOptions}
                                    onChange={(value) => {
                                        const members = Array.from(
                                            new Set([
                                                ...(group.member_user_ids ||
                                                    []),
                                                value,
                                            ])
                                        );
                                        updateGroup(index, {
                                            head_user_id: value,
                                            member_user_ids: members,
                                        });
                                    }}
                                />
                            </DBFieldBlock>
                            <DBFieldBlock
                                title={t("ai.handling.group.members")}
                            >
                                <DBSelect
                                    size="large"
                                    mode="multiple"
                                    value={group.member_user_ids || []}
                                    options={userOptions}
                                    onChange={(value) => {
                                        const members = value || [];
                                        const head =
                                            members.includes(group.head_user_id)
                                                ? group.head_user_id
                                                : members[0];
                                        updateGroup(index, {
                                            member_user_ids: members,
                                            head_user_id: head,
                                        });
                                    }}
                                />
                            </DBFieldBlock>
                        </div>
                    </li>
                ))}
            </ul>
        </div>
    );

    const renderAssignStep = () => (
        <div className="ai-preview ai-assign-preview">
            <h3>{t("ai.handling.assign.title")}</h3>
            <p>{t("ai.handling.assign.description")}</p>
            <ul className="ai-assign-list">
                {pendingAssignments.map((item) => (
                    <li key={item.item_type_id}>
                        <div className="ai-assign-item-type">
                            <span>{t("ai.handling.assign.item.type")}</span>
                            <strong>{item.item_type_name}</strong>
                        </div>
                        <div className="ai-assign-meta">
                            <div>
                                <span>{t("ai.handling.assign.handling")}</span>
                                <em>{item.handling_group_name}</em>
                            </div>
                            <div>
                                <span>
                                    {t("ai.handling.assign.supervisor")}
                                </span>
                                <em>
                                    {(item.supervisor_group_names || []).join(
                                        "، "
                                    )}
                                </em>
                            </div>
                        </div>
                    </li>
                ))}
            </ul>
        </div>
    );

    const showWizard = rolesGate === "ready" && !pendingAssignments;
    const showGate = rolesGate !== "ready" && !pendingAssignments;

    return (
        <>
        <DBModal
            permanent
            show={showModal}
            minWidth={760}
            onHide={onHide}
            className="ai-handling-group-modal"
        >
            <DBModal.Header closeBtn={onHide} title={t("ai.handling.title")}>
                <MagicStar size={24} aria-hidden="true" />
            </DBModal.Header>
            <DBModal.Content>
                {pendingAssignments ? (
                    renderAssignStep()
                ) : showGate ? (
                    renderGate()
                ) : !groups ? (
                    <>
                        <div className="ai-progress">
                            <span>
                                {t("ai.handling.step", { step: step + 1 })}
                            </span>
                            <div
                                role="progressbar"
                                aria-valuenow={step + 1}
                                aria-valuemin={1}
                                aria-valuemax={5}
                                aria-label={t("ai.handling.step", {
                                    step: step + 1,
                                })}
                            >
                                {[0, 1, 2, 3, 4].map((item) => (
                                    <i
                                        key={item}
                                        className={item <= step ? "active" : ""}
                                    />
                                ))}
                            </div>
                        </div>
                        <div className="ai-question">{renderQuestion()}</div>
                    </>
                ) : (
                    renderPreview()
                )}
            </DBModal.Content>
            <DBModal.Footer>
                {pendingAssignments ? (
                    <>
                        <DBButton
                            caption={t("ai.handling.assign.confirm")}
                            onClick={confirmAssignments}
                            loading={assignLoader}
                        />
                        <DBButton
                            caption={t("ai.handling.assign.skip")}
                            type="secondary"
                            onClick={skipAssignments}
                        />
                    </>
                ) : showWizard && !groups ? (
                    <>
                        {step === 4 ? (
                            <DBButton
                                caption={
                                    <>
                                        {t("ai.handling.generate")}
                                        <AiCoinHint operation="handling_groups" />
                                    </>
                                }
                                onClick={requestGroups}
                                loading={generateLoader}
                            />
                        ) : (
                            <DBButton
                                caption={t("ai.handling.next")}
                                onClick={nextStep}
                            />
                        )}
                        {step > 0 && (
                            <DBButton
                                caption={t("ai.handling.previous")}
                                type="secondary"
                                onClick={() => {
                                    setStep(step - 1);
                                    setError("");
                                }}
                            />
                        )}
                    </>
                ) : showWizard && groups ? (
                    <>
                        <DBButton
                            caption={t("ai.handling.save")}
                            onClick={saveGroups}
                            loading={applyLoader}
                        />
                        <DBButton
                            caption={t("ai.handling.regenerate")}
                            type="secondary"
                            onClick={() => {
                                setGroups(undefined);
                                setStep(4);
                            }}
                        />
                    </>
                ) : null}
                <DBButton
                    caption={t("cancel")}
                    type="secondary"
                    onClick={onHide}
                />
            </DBModal.Footer>
        </DBModal>
        {gate}
        </>
    );
}
